import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ControllerTypes } from 'src/enums'
import { ICustomProperty } from 'src/generated-types/customProperty'
import { MUIThemeColors } from 'src/mui-theme'
import { resolveCustomPropertyLabel } from 'src/utils/customPropertyValues'

export interface FieldTypeMeta {
  badge: string
  color: MUIThemeColors
  labelKey: string
}

export const FIELD_TYPE_META: Record<ControllerTypes, FieldTypeMeta> = {
  [ControllerTypes.TextBox]: { badge: 'T', color: MUIThemeColors.secondaryDark, labelKey: 'TextBox' },
  [ControllerTypes.NumberTextBox]: { badge: '#', color: MUIThemeColors.primaryLight, labelKey: 'NumberTextBox' },
  [ControllerTypes.Dropdown]: { badge: '≡', color: MUIThemeColors.infoMain, labelKey: 'Dropdown' },
}

export const getFieldTypeMeta = (controllerType: ControllerTypes): FieldTypeMeta =>
  FIELD_TYPE_META[controllerType] ?? FIELD_TYPE_META[ControllerTypes.TextBox]

interface UseEquipmentFieldPickerArgs {
  customProperties: ICustomProperty[]
  setCustomProperties: React.Dispatch<React.SetStateAction<ICustomProperty[]>>
  data: ICustomProperty[]
  lockedCustomPropertyIds?: number[]
}

export const useEquipmentFieldPicker = ({
  customProperties,
  setCustomProperties,
  data,
  lockedCustomPropertyIds = [],
}: UseEquipmentFieldPickerArgs) => {
  const pickerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draggingId, setDraggingId] = useState<number | null>(null)

  const selectedIds = useMemo(() => new Set(customProperties.map(p => p.id)), [customProperties])

  const q = query.trim().toLowerCase()
  const available = useMemo(
    () =>
      data.filter(
        property =>
          !selectedIds.has(property.id) && (!q || resolveCustomPropertyLabel(property).toLowerCase().includes(q)),
      ),
    [data, selectedIds, q],
  )

  const addProperty = useCallback(
    (property: ICustomProperty) => {
      setCustomProperties(prev => (prev.some(p => p.id === property.id) ? prev : [...prev, property]))
    },
    [setCustomProperties],
  )

  const removeProperty = useCallback(
    (id: number) => {
      if (lockedCustomPropertyIds.includes(id)) return
      setCustomProperties(prev => prev.filter(p => p.id !== id))
    },
    [setCustomProperties, lockedCustomPropertyIds],
  )

  const reorder = useCallback(
    (dragId: number, targetId: number) => {
      if (dragId === targetId) return
      setCustomProperties(prev => {
        const fromIndex = prev.findIndex(p => p.id === dragId)
        const toIndex = prev.findIndex(p => p.id === targetId)
        if (fromIndex === -1 || toIndex === -1) return prev
        const updated = [...prev]
        const [dragged] = updated.splice(fromIndex, 1)
        updated.splice(toIndex, 0, dragged)
        return updated
      })
    },
    [setCustomProperties],
  )

  const moveToEnd = useCallback(
    (dragId: number) => {
      setCustomProperties(prev => {
        const dragged = prev.find(p => p.id === dragId)
        if (!dragged) return prev
        const rest = prev.filter(p => p.id !== dragId)
        return [...rest, dragged]
      })
    },
    [setCustomProperties],
  )

  const focusSearch = useCallback(() => {
    setPickerOpen(true)
    searchInputRef.current?.focus()
  }, [])

  return {
    pickerRef,
    searchInputRef,
    query,
    setQuery,
    pickerOpen,
    setPickerOpen,
    draggingId,
    setDraggingId,
    available,
    addProperty,
    removeProperty,
    reorder,
    moveToEnd,
    focusSearch,
  }
}
